import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download, Search, X } from "lucide-react";
import { listarMarcacoes, exportCsvUrl } from "../../lib/adminApi";
import { Card, EstadoBadge, MarcacaoLink, PageTitle, ClinicaLabel, formatDate } from "../ui";

const ESTADOS = ["pendente", "confirmada", "concluida", "cancelada", "no_show"];

export default function AdminMarcacoesPage() {
  const [search, setSearch] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const params = {
    q: search.get("q") || "",
    clinica: search.get("clinica") || "",
    estado: search.get("estado") || "",
    data_inicio: search.get("data_inicio") || "",
    data_fim: search.get("data_fim") || "",
    page: Number(search.get("page") || 1),
    page_size: 50,
  };

  const carregar = useCallback(async () => {
    setLoading(true);
    const cleaned = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "" && v != null));
    try {
      const r = await listarMarcacoes(cleaned);
      setData(r);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => { carregar(); }, [carregar]);

  const update = (patch) => {
    const next = new URLSearchParams(search);
    Object.entries(patch).forEach(([k, v]) => {
      if (!v) next.delete(k); else next.set(k, v);
    });
    if (!("page" in patch)) next.delete("page");
    setSearch(next);
  };

  const limpar = () => setSearch({});

  const toggleEstado = (v) => {
    const list = (params.estado || "").split(",").filter(Boolean);
    const i = list.indexOf(v);
    if (i >= 0) list.splice(i, 1); else list.push(v);
    update({ estado: list.join(",") });
  };

  const csvHref = exportCsvUrl(Object.fromEntries(Object.entries(params).filter(([, v]) => v)));

  return (
    <div data-testid="admin-marcacoes-list">
      <PageTitle subtitle="Lista e gestão de marcações.">Marcações</PageTitle>

      <Card className="mb-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4">
            <label className="text-xs font-bold uppercase tracking-widest text-[#88A096]">Pesquisar</label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A5A5A]" />
              <input
                data-testid="filter-q"
                defaultValue={params.q}
                placeholder="nome, email, animal, número"
                className="w-full rounded-xl bg-white border-2 border-[#1E5F6E]/10 pl-10 pr-3 py-2.5 text-sm focus:border-[#1E5F6E] focus:outline-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter") update({ q: e.target.value });
                }}
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <label className="text-xs font-bold uppercase tracking-widest text-[#88A096]">Clínica</label>
            <select
              data-testid="filter-clinica"
              value={params.clinica}
              onChange={(e) => update({ clinica: e.target.value })}
              className="mt-1 w-full rounded-xl bg-white border-2 border-[#1E5F6E]/10 px-3 py-2.5 text-sm"
            >
              <option value="">Todas</option>
              <option value="ribeira-brava">Ribeira Brava</option>
              <option value="calheta">Calheta</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-[#88A096]">De</label>
            <input
              type="date" data-testid="filter-data-inicio"
              defaultValue={params.data_inicio}
              onBlur={(e) => update({ data_inicio: e.target.value })}
              className="mt-1 w-full rounded-xl bg-white border-2 border-[#1E5F6E]/10 px-3 py-2.5 text-sm"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-[#88A096]">Até</label>
            <input
              type="date" data-testid="filter-data-fim"
              defaultValue={params.data_fim}
              onBlur={(e) => update({ data_fim: e.target.value })}
              className="mt-1 w-full rounded-xl bg-white border-2 border-[#1E5F6E]/10 px-3 py-2.5 text-sm"
            />
          </div>
          <div className="md:col-span-1 flex gap-2 justify-end">
            <button onClick={limpar} className="rounded-xl bg-white border-2 border-[#1E5F6E]/10 px-3 py-2.5 text-xs font-bold text-[#1E5F6E]" data-testid="btn-clear-filters">
              <X className="h-3.5 w-3.5 inline" /> Limpar
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-widest font-bold text-[#88A096] mr-2">Estado:</span>
          {ESTADOS.map((e) => {
            const sel = (params.estado || "").split(",").includes(e);
            return (
              <button
                key={e}
                onClick={() => toggleEstado(e)}
                data-testid={`filter-estado-${e}`}
                className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
                  sel ? "bg-[#1E5F6E] text-white border-[#1E5F6E]" : "bg-white text-[#5A5A5A] border-[#1E5F6E]/15 hover:border-[#1E5F6E]/40"
                }`}
              >{e}</button>
            );
          })}
        </div>
      </Card>

      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-[#5A5A5A]" data-testid="total-count">{data.total} resultados</div>
        <a
          href={csvHref}
          data-testid="btn-export-csv"
          className="inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] px-4 py-2 text-xs font-bold text-white hover:bg-[#174E5B]"
        >
          <Download className="h-3.5 w-3.5" />
          Exportar CSV
        </a>
      </div>

      <Card className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF7F2] text-xs uppercase tracking-wider text-[#5A5A5A]">
              <tr>
                <th className="text-left py-3 px-4">Número</th>
                <th className="text-left py-3 px-4">Data/Hora</th>
                <th className="text-left py-3 px-4">Cliente</th>
                <th className="text-left py-3 px-4">Animal</th>
                <th className="text-left py-3 px-4">Clínica</th>
                <th className="text-left py-3 px-4">Serviço</th>
                <th className="text-left py-3 px-4">Estado</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="py-6 text-center text-[#5A5A5A]">A carregar...</td></tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-[#5A5A5A]">Sem resultados.</td></tr>
              )}
              {!loading && data.items.map((m) => (
                <tr key={m.id} className="border-t border-[#1E5F6E]/10 hover:bg-[#FAF7F2]" data-testid={`row-${m.id}`}>
                  <td className="py-3 px-4 font-semibold"><MarcacaoLink id={m.id}>{m.numero_marcacao}</MarcacaoLink></td>
                  <td className="py-3 px-4">{formatDate(m.data_hora)}</td>
                  <td className="py-3 px-4">
                    <div className="font-semibold">{m.tutor.nome}</div>
                    <div className="text-xs text-[#5A5A5A]">{m.tutor.email}</div>
                  </td>
                  <td className="py-3 px-4">{m.animal.nome} <span className="text-xs text-[#5A5A5A] capitalize">({m.animal.especie})</span></td>
                  <td className="py-3 px-4"><ClinicaLabel slug={m.clinica_slug} /></td>
                  <td className="py-3 px-4">{m.servico_slug}</td>
                  <td className="py-3 px-4"><EstadoBadge estado={m.estado} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-4 flex items-center justify-between">
        <button
          disabled={params.page <= 1}
          onClick={() => update({ page: String(params.page - 1) })}
          data-testid="btn-prev-page"
          className="rounded-full bg-white border-2 border-[#1E5F6E]/15 px-4 py-2 text-xs font-bold text-[#1E5F6E] disabled:opacity-40"
        >← Anterior</button>
        <span className="text-xs text-[#5A5A5A]">Página {params.page}</span>
        <button
          disabled={params.page * params.page_size >= data.total}
          onClick={() => update({ page: String(params.page + 1) })}
          data-testid="btn-next-page"
          className="rounded-full bg-white border-2 border-[#1E5F6E]/15 px-4 py-2 text-xs font-bold text-[#1E5F6E] disabled:opacity-40"
        >Seguinte →</button>
      </div>
    </div>
  );
}
